import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Calendar, Filter, TrendingUp, TrendingDown, DollarSign, PieChart, X, Check, Clock, CreditCard, CalendarDays, Search, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#7FA1C3', '#F59E0B', '#90D26D', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [profitSummary, setProfitSummary] = useState(null);
  
  // Form state
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseIsPaid, setExpenseIsPaid] = useState(false);
  const [expensePaymentDate, setExpensePaymentDate] = useState('');
  const [expenseDeductFromRevenue, setExpenseDeductFromRevenue] = useState(true);
  
  // New category form
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  
  // Filter state
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // UPI data for date-filtered view
  const [upiForDates, setUpiForDates] = useState(null);

  // Mark as Paid modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payExpenseId, setPayExpenseId] = useState(null);
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
    fetchDailySummary();
    fetchProfitSummary();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      let url = `${API}/expenses`;
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchDailySummary = async () => {
    try {
      let url = `${API}/expenses/summary/daily`;
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setDailySummary(response.data);
    } catch (error) {
      console.error('Error fetching daily summary:', error);
    }
  };

  const fetchProfitSummary = async () => {
    try {
      let url = `${API}/analytics/profit-summary`;
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setProfitSummary(response.data);
    } catch (error) {
      console.error('Error fetching profit summary:', error);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseDate || !expenseAmount || !expenseCategory) {
      toast.error('Please fill in date, amount, and category');
      return;
    }
    const amount = parseFloat(expenseAmount);
    if (amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    try {
      await axios.post(`${API}/expenses`, {
        date: expenseDate,
        amount,
        category: expenseCategory,
        description: expenseDescription,
        is_paid: expenseIsPaid,
        payment_date: expenseIsPaid ? (expensePaymentDate || expenseDate) : null,
        deduct_from_revenue: expenseDeductFromRevenue,
      });
      toast.success(`Expense added as ${expenseIsPaid ? 'PAID' : 'UNPAID'}`);
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseIsPaid(false);
      setExpensePaymentDate('');
      setExpenseDeductFromRevenue(true);
      fetchExpenses();
      fetchDailySummary();
      fetchProfitSummary();
    } catch (error) {
      toast.error('Error adding expense');
      console.error(error);
    }
  };

  const openPayModal = (expenseId) => {
    setPayExpenseId(expenseId);
    setPayDate(new Date().toISOString().split('T')[0]);
    setShowPayModal(true);
  };

  const handleMarkPaid = async () => {
    if (!payDate) {
      toast.error('Please select a payment date');
      return;
    }
    try {
      await axios.patch(`${API}/expenses/${payExpenseId}/pay`, { payment_date: payDate });
      toast.success(`Expense marked as PAID on ${payDate}`);
      setShowPayModal(false);
      setPayExpenseId(null);
      fetchExpenses();
      fetchProfitSummary();
    } catch (error) {
      toast.error('Error marking expense as paid');
      console.error(error);
    }
  };

  const handleToggleDeduct = async (expenseId) => {
    try {
      const res = await axios.patch(`${API}/expenses/${expenseId}/toggle-deduct`);
      toast.success(res.data.deduct_from_revenue ? 'Will deduct from revenue' : 'Marked as pending liability');
      fetchExpenses();
      fetchProfitSummary();
    } catch (error) {
      toast.error('Error updating expense');
      console.error(error);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchDailySummary();
      fetchProfitSummary();
    } catch (error) {
      toast.error('Error deleting expense');
      console.error(error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      await axios.post(`${API}/expense-categories`, { name: newCategoryName.trim() });
      toast.success(`Category "${newCategoryName}" added`);
      setNewCategoryName('');
      setShowAddCategory(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding category');
      console.error(error);
    }
  };

  const applyFilters = () => {
    if (filterStartDate && filterEndDate && filterStartDate > filterEndDate) {
      toast.error('Start date must be before end date');
      return;
    }
    fetchExpenses();
    fetchDailySummary();
    fetchProfitSummary();
    // Fetch UPI data when date filter is active
    if (filterStartDate || filterEndDate) {
      fetchUpiForDates();
    } else {
      setUpiForDates(null);
    }
    if (filterStartDate || filterEndDate || filterCategory !== 'all' || searchQuery) {
      toast.success(`Filter applied`);
    }
  };

  const fetchUpiForDates = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      const res = await axios.get(`${API}/analytics/upi-collection?${params.toString()}`);
      setUpiForDates(res.data);
    } catch (error) {
      console.error('Error fetching UPI data:', error);
    }
  };

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterCategory('all');
    setSearchQuery('');
    setUpiForDates(null);
    setTimeout(() => {
      fetchExpenses();
      fetchDailySummary();
      fetchProfitSummary();
      toast.info('Filters cleared');
    }, 100);
  };

  const isFilterActive = filterStartDate || filterEndDate || (filterCategory && filterCategory !== 'all') || searchQuery;
  const paidExpenses = expenses.filter(exp => exp.is_paid).reduce((sum, exp) => sum + exp.amount, 0);
  const unpaidExpenses = expenses.filter(exp => !exp.is_paid).reduce((sum, exp) => sum + exp.amount, 0);

  const exportExpensesToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Expenses List
    const expData = [['Date', 'Category', 'Description', 'Amount (₹)', 'Status', 'Payment Date', 'Deduct from Revenue']];
    expenses.forEach(exp => {
      expData.push([
        exp.date,
        exp.category,
        exp.description || '-',
        exp.amount,
        exp.is_paid ? 'Paid' : 'Unpaid',
        exp.payment_date || '-',
        exp.deduct_from_revenue !== false ? 'Yes' : 'No',
      ]);
    });
    expData.push([]);
    expData.push(['', '', 'Paid Total', paidExpenses]);
    expData.push(['', '', 'Unpaid Total', unpaidExpenses]);
    const ws1 = XLSX.utils.aoa_to_sheet(expData);
    ws1['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Expenses');

    // Sheet 2: Summary
    if (profitSummary) {
      const sumData = [
        ['Metric', 'Value (₹)'],
        ['Paid Expenses', profitSummary.total_expenses],
        ['Unpaid (Pending)', profitSummary.total_unpaid],
        ['Factory Revenue', profitSummary.total_sales],
        ['Net Profit', profitSummary.net_profit],
      ];
      if (profitSummary.expense_by_category) {
        sumData.push([]);
        sumData.push(['Category Breakdown', '']);
        Object.entries(profitSummary.expense_by_category).forEach(([cat, amt]) => {
          sumData.push([cat, amt]);
        });
      }
      const ws2 = XLSX.utils.aoa_to_sheet(sumData);
      ws2['!cols'] = [{ wch: 25 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Summary');
    }

    // Sheet 3: UPI Data (if date filtered)
    if (upiForDates && upiForDates.vendor_upi_details?.length > 0) {
      const upiData = [['Salesman', 'UPI Amount (₹)', 'Date']];
      upiForDates.vendor_upi_details.forEach(u => upiData.push([u.vendor_name, u.upi_amount, u.date]));
      upiData.push([]);
      upiData.push(['Total UPI', upiForDates.total_upi]);
      upiData.push(['Total Cash', upiForDates.total_cash]);
      const ws3 = XLSX.utils.aoa_to_sheet(upiData);
      ws3['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'UPI Collections');
    }

    const dateStr = filterStartDate && filterEndDate ? `${filterStartDate}_${filterEndDate}` : 'All';
    XLSX.writeFile(wb, `Convella_Expenses_${dateStr}.xlsx`);
    toast.success('Expenses exported to Excel');
  };

  const pieData = profitSummary ? Object.entries(profitSummary.expense_by_category).map(([name, value]) => ({
    name,
    value
  })) : [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-[#7FA1C3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Expense Tracker
          </h1>
          <p className="text-slate-600 mt-2">Track daily expenses and monitor profitability</p>
        </div>
        <Button
          onClick={exportExpensesToExcel}
          data-testid="export-expenses-excel"
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-semibold"
        >
          <Download size={16} className="mr-2" />
          Export to Excel
        </Button>
      </div>

      {/* Date Range Filter Card */}
      <Card className={`shadow-sm ${isFilterActive ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-[#7FA1C3]" size={20} />
              <span className="font-semibold text-slate-700">Filter by Date Range:</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">From:</Label>
                <Input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">To:</Label>
                <Input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-40"
                  data-testid="filter-end-date"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-36" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={applyFilters}
                data-testid="apply-filter-btn"
                className="bg-[#7FA1C3] hover:bg-[#6482A6] text-white px-6"
              >
                <Filter size={16} className="mr-2" />
                Apply Filter
              </Button>
              {isFilterActive && (
                <Button
                  onClick={clearFilters}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4"
                >
                  <X size={16} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          {/* Search Bar */}
          <div className="mt-3 flex items-center gap-3">
            <Search size={18} className="text-[#7FA1C3]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="Search expenses (e.g., Cylinder, Milk, Labour)..."
              data-testid="expense-search-input"
              className="flex-1"
            />
          </div>
          {isFilterActive && (
            <div className="mt-3 p-2 bg-blue-100 rounded-lg text-sm text-blue-800 flex items-center gap-2">
              <Filter size={14} />
              <span>
                <strong>Active Filter:</strong>{' '}
                {filterStartDate && filterEndDate 
                  ? `${filterStartDate} to ${filterEndDate}`
                  : filterStartDate ? `From ${filterStartDate}` : filterEndDate ? `Until ${filterEndDate}` : ''}
                {filterCategory !== 'all' && ` | Category: ${filterCategory}`}
                {' '}— Showing {expenses.length} expense(s)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-red-100">
                <TrendingDown className="text-red-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Paid Expenses</p>
                <p className="text-2xl font-bold text-red-600" data-testid="paid-expenses-total">₹{profitSummary?.total_expenses?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-orange-100">
                <Clock className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Unpaid (Pending)</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="unpaid-expenses-total">₹{profitSummary?.total_unpaid?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-green-100">
                <TrendingUp className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Factory Revenue</p>
                <p className="text-2xl font-bold text-green-600">₹{profitSummary?.total_sales?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${profitSummary?.net_profit >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                <DollarSign className={profitSummary?.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'} size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Net Profit</p>
                <p className={`text-2xl font-bold ${profitSummary?.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ₹{profitSummary?.net_profit?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-slate-500">(Revenue - Deducted Expenses)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-purple-100">
                <PieChart className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Expense Entries</p>
                <p className="text-2xl font-bold text-purple-600">{expenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* UPI Collection Summary - shown when date filter is active */}
      {upiForDates && (filterStartDate || filterEndDate) && (
        <Card className="shadow-sm border-purple-200 bg-purple-50/50" data-testid="upi-date-summary">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b py-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <CreditCard size={18} className="text-purple-600" />
              UPI Collections for Selected Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center gap-6 mb-3">
              <div>
                <p className="text-sm text-slate-600">Total UPI</p>
                <p className="text-xl font-bold text-purple-600">₹{upiForDates.total_upi.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Cash</p>
                <p className="text-xl font-bold text-green-600">₹{upiForDates.total_cash.toFixed(2)}</p>
              </div>
            </div>
            {upiForDates.vendor_upi_details && upiForDates.vendor_upi_details.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="upi-vendor-table-expenses">
                  <thead>
                    <tr className="border-b border-purple-200">
                      <th className="text-left py-2 px-3 font-semibold">Salesman</th>
                      <th className="text-right py-2 px-3 font-semibold">UPI Amount</th>
                      <th className="text-left py-2 px-3 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upiForDates.vendor_upi_details.map((entry, idx) => (
                      <tr key={idx} className="border-b border-purple-100 hover:bg-purple-50/50">
                        <td className="py-2 px-3">{entry.vendor_name}</td>
                        <td className="py-2 px-3 text-right font-semibold text-purple-600">₹{entry.upi_amount.toFixed(2)}</td>
                        <td className="py-2 px-3 text-slate-600">{entry.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Expense Form */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b">
            <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Plus size={20} />
              Add Expense
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <Label htmlFor="expense-date">Expense Date</Label>
                <Input
                  id="expense-date"
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  data-testid="expense-date-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="expense-amount">Amount (₹)</Label>
                <Input
                  id="expense-amount"
                  type="number"
                  step="0.01"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  placeholder="Enter amount"
                  data-testid="expense-amount-input"
                  className="mt-1"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="expense-category">Category</Label>
                  <button
                    type="button"
                    onClick={() => setShowAddCategory(!showAddCategory)}
                    className="text-xs text-[#7FA1C3] hover:underline"
                  >
                    + Add Category
                  </button>
                </div>
                
                {showAddCategory && (
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      className="flex-1 text-sm"
                    />
                    <Button
                      type="button"
                      onClick={handleAddCategory}
                      className="bg-[#7FA1C3] hover:bg-[#6482A6] text-white text-sm px-3"
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowAddCategory(false)}
                      className="bg-slate-200 hover:bg-slate-300 text-slate-600 text-sm px-2"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                )}
                
                <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                  <SelectTrigger data-testid="expense-category-select" className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="expense-description">Description (optional)</Label>
                <Input
                  id="expense-description"
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                  placeholder="e.g., Morning milk delivery"
                  data-testid="expense-description-input"
                  className="mt-1"
                />
              </div>

              {/* Payment Status */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <Label className="flex-1">Status:</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => { setExpenseIsPaid(false); setExpensePaymentDate(''); }}
                    className={`px-4 py-2 text-sm rounded-full ${
                      !expenseIsPaid 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                  >
                    <Clock size={14} className="mr-1" />
                    Unpaid
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setExpenseIsPaid(true)}
                    className={`px-4 py-2 text-sm rounded-full ${
                      expenseIsPaid 
                        ? 'bg-green-500 text-white' 
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                  >
                    <Check size={14} className="mr-1" />
                    Paid
                  </Button>
                </div>
              </div>

              {/* Payment Date - shown only when Paid */}
              {expenseIsPaid && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Label htmlFor="payment-date" className="text-sm font-semibold text-green-800">Payment Date</Label>
                  <Input
                    id="payment-date"
                    type="date"
                    value={expensePaymentDate}
                    onChange={(e) => setExpensePaymentDate(e.target.value)}
                    data-testid="expense-payment-date-input"
                    className="mt-1"
                  />
                  <p className="text-xs text-green-700 mt-1">Leave empty to use expense date as payment date</p>
                </div>
              )}

              {/* Deduct from Revenue Checkbox */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="deduct-revenue"
                  checked={expenseDeductFromRevenue}
                  onChange={(e) => setExpenseDeductFromRevenue(e.target.checked)}
                  data-testid="deduct-from-revenue-checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <Label htmlFor="deduct-revenue" className="text-sm font-semibold cursor-pointer">
                    Deduct from Revenue
                  </Label>
                  <p className="text-xs text-slate-500">
                    {expenseDeductFromRevenue 
                      ? 'Will be subtracted from total revenue' 
                      : 'Treated as pending liability (not deducted)'}
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="add-expense-button"
                className="w-full bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full"
              >
                <Plus size={18} className="mr-2" />
                Add Expense
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Charts */}
        <Card className="shadow-sm border-slate-200 lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-sm mb-3">Daily Expenses</h3>
                {dailySummary.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={dailySummary.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                      <Bar dataKey="total" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-400">No expense data available</div>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-3">By Category</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <RechartsPie>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-400">No category data available</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expense List */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Expense List ({expenses.length} {isFilterActive ? 'filtered' : 'total'})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="expenses-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-3 font-semibold text-sm">Expense Date</th>
                    <th className="text-left py-3 px-3 font-semibold text-sm">Category</th>
                    <th className="text-left py-3 px-3 font-semibold text-sm">Description</th>
                    <th className="text-right py-3 px-3 font-semibold text-sm">Amount</th>
                    <th className="text-center py-3 px-3 font-semibold text-sm">Status</th>
                    <th className="text-center py-3 px-3 font-semibold text-sm">Payment Date</th>
                    <th className="text-center py-3 px-3 font-semibold text-sm">Deduct</th>
                    <th className="text-center py-3 px-3 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr
                      key={expense.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        !expense.is_paid ? 'bg-orange-50/50' : ''
                      }`}
                      data-testid={`expense-row-${expense.id}`}
                    >
                      <td className="py-3 px-3 text-sm">{expense.date}</td>
                      <td className="py-3 px-3 text-sm">
                        <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-medium">
                          {expense.category}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-slate-600">{expense.description || '-'}</td>
                      <td className="py-3 px-3 text-sm text-right font-semibold text-red-600">
                        ₹{expense.amount.toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-center">
                        {expense.is_paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <Check size={12} />
                            Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            <Clock size={12} />
                            Unpaid
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center text-sm">
                        {expense.payment_date ? (
                          <span className="text-green-700 font-medium">{expense.payment_date}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <button
                          onClick={() => handleToggleDeduct(expense.id)}
                          data-testid={`toggle-deduct-${expense.id}`}
                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            expense.deduct_from_revenue !== false
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          title={expense.deduct_from_revenue !== false ? 'Deducted from revenue' : 'Pending liability'}
                        >
                          {expense.deduct_from_revenue !== false ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {!expense.is_paid && (
                            <Button
                              onClick={() => openPayModal(expense.id)}
                              data-testid={`mark-paid-${expense.id}`}
                              className="px-2 py-1 h-auto bg-green-500 hover:bg-green-600 text-white rounded-full text-xs"
                              title="Mark as paid with date"
                            >
                              <CalendarDays size={12} className="mr-1" />
                              Mark Paid
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteExpense(expense.id)}
                            data-testid={`delete-expense-${expense.id}`}
                            className="p-2 h-auto bg-red-500 hover:bg-red-600 text-white rounded-full"
                            title="Delete expense"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td colSpan={3} className="py-3 px-3 text-sm text-right font-semibold">Paid Total:</td>
                    <td className="py-3 px-3 text-sm text-right font-bold text-red-600">₹{paidExpenses.toFixed(2)}</td>
                    <td colSpan={4}></td>
                  </tr>
                  <tr className="bg-orange-50">
                    <td colSpan={3} className="py-3 px-3 text-sm text-right font-semibold">Unpaid Total:</td>
                    <td className="py-3 px-3 text-sm text-right font-bold text-orange-600">₹{unpaidExpenses.toFixed(2)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <p>No expenses found. Add your first expense above!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mark as Paid Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <CalendarDays className="text-green-600" />
              Mark Expense as Paid
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Select the date when this payment was made. The original expense date will remain unchanged.
            </p>
            <div>
              <Label htmlFor="pay-date" className="text-sm font-semibold">Payment Date</Label>
              <Input
                id="pay-date"
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                data-testid="pay-date-input"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowPayModal(false)}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              data-testid="confirm-mark-paid-btn"
              className="bg-green-500 hover:bg-green-600 text-white rounded-full"
            >
              <Check size={16} className="mr-2" />
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
